import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { workspacesApi, wordpressApi } from '../lib/api';
import { Alert, BackLink } from '@rankhelm/ui';
import type { WordPressTestResult, SeoPlugin } from '@rankhelm/shared';

export function WorkspaceCreatePage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    website_url: '',
    linkedin_url: '',
    publish_target_type: 'wordpress' as const,
    wp_url: '',
    wp_username: '',
    wp_app_password: '',
    seo_plugin: '' as string,
    nap_enabled: false,
    nap_business_name: '',
    nap_address: '',
    nap_city: '',
    nap_state: '',
    nap_zip: '',
    nap_phone: '',
  });
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<WordPressTestResult | null>(null);
  const [connectionVerified, setConnectionVerified] = useState(false);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm({ ...form, [field]: value });
    if (['wp_url', 'wp_username', 'wp_app_password'].includes(field)) {
      setConnectionVerified(false);
      setTestResult(null);
    }
  };

  const handleTestConnection = async () => {
    if (!form.wp_url || !form.wp_username || !form.wp_app_password) {
      setTestResult({ connected: false, error: 'Please fill in all WordPress connection fields first.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    setConnectionVerified(false);
    try {
      const res = await wordpressApi.testConnection({
        url: form.wp_url,
        app_password: form.wp_app_password,
        username: form.wp_username,
      });
      setTestResult(res.data);
      if (res.data.connected && res.data.can_publish) {
        setConnectionVerified(true);
      }
    } catch (err) {
      setTestResult({ connected: false, can_publish: false, error: err instanceof Error ? err.message : 'Connection failed' } as WordPressTestResult);
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!connectionVerified) {
      setError('Please test your WordPress connection before creating the workspace.');
      return;
    }

    setCreating(true);
    try {
      const napDataJson = form.nap_enabled ? JSON.stringify({
        business_name: form.nap_business_name,
        address: form.nap_address,
        city: form.nap_city,
        state: form.nap_state,
        zip: form.nap_zip,
        phone: form.nap_phone,
      }) : null;

      await workspacesApi.create({
        name: form.name,
        website_url: form.website_url || null,
        linkedin_url: form.linkedin_url || null,
        publish_target_type: form.publish_target_type,
        publish_config: {
          url: form.wp_url,
          app_password_enc: form.wp_app_password,
          username: form.wp_username,
        },
        seo_plugin: (form.seo_plugin || null) as SeoPlugin | null,
        nap_enabled: form.nap_enabled,
        nap_data_json: napDataJson,
      });
      navigate('/workspaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <BackLink to="/workspaces" label="Back to Workspaces" />
      <h1 className="text-2xl font-bold text-white mb-6">Create Workspace</h1>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white">General</h2>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Workspace Name</label>
            <input type="text" value={form.name} onChange={update('name')} required
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="e.g., Acme Roofing" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Website URL (optional)</label>
            <input type="url" value={form.website_url} onChange={update('website_url')}
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="https://acmeroofing.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">LinkedIn URL (optional)</label>
            <input type="url" value={form.linkedin_url} onChange={update('linkedin_url')}
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="https://www.linkedin.com/company/acme" />
            <p className="text-xs text-slate-500 mt-1">Used by the Business Context AI research to pull public company info.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Publish Target</label>
            <select value={form.publish_target_type} onChange={update('publish_target_type')}
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500">
              <option value="wordpress">WordPress</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">More publishing targets coming soon.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">SEO Plugin</label>
            <select value={form.seo_plugin} onChange={update('seo_plugin')}
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500">
              <option value="">None / Other</option>
              <option value="yoast">Yoast SEO</option>
              <option value="rankmath">RankMath</option>
              <option value="aioseo">All in One SEO (AIOSEO)</option>
            </select>
          </div>
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white">WordPress Connection</h2>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">WordPress Site URL</label>
            <input type="url" value={form.wp_url} onChange={update('wp_url')} required
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="https://yoursite.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">WordPress Username</label>
            <input type="text" value={form.wp_username} onChange={update('wp_username')} required
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="admin" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Application Password</label>
            <input type="password" value={form.wp_app_password} onChange={update('wp_app_password')} required
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" />
            <p className="text-xs text-slate-500 mt-1">Generate in WordPress: Users → Profile → Application Passwords</p>
          </div>
          <div>
            <button type="button" onClick={handleTestConnection} disabled={testing || !form.wp_url || !form.wp_username || !form.wp_app_password}
              className="px-5 py-2.5 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm font-medium rounded-lg border border-navy-600 transition-colors disabled:opacity-50">
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
          {testResult && (
            <div className={`text-sm rounded-lg px-4 py-3 ${testResult.connected && testResult.can_publish ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-orange-500/10 border border-orange-500/30 text-orange-400'}`}>
              {testResult.connected && testResult.can_publish ? (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Connected to <strong>{testResult.site_name}</strong> (WP {testResult.wp_version})</span>
                </div>
              ) : testResult.connected ? (
                <span>Site found ({testResult.site_name}) but: {testResult.error}</span>
              ) : (
                <span>{testResult.error || 'Connection failed.'}</span>
              )}
            </div>
          )}
          {!connectionVerified && <p className="text-xs text-slate-500">You must successfully test the connection before creating this workspace.</p>}
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.nap_enabled} onChange={update('nap_enabled')} className="w-4 h-4 rounded border-navy-600 text-helm-500 focus:ring-helm-500 bg-navy-800" />
            <div>
              <span className="text-lg font-semibold text-white">NAP Schema (Local Business)</span>
              <p className="text-xs text-slate-400">Injects LocalBusiness schema into published articles. For physical businesses.</p>
            </div>
          </label>
          {form.nap_enabled && (
            <div className="space-y-4 pt-2 border-t border-navy-700">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Business Name</label>
                <input type="text" value={form.nap_business_name} onChange={update('nap_business_name')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="Acme Roofing LLC" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Street Address</label>
                <input type="text" value={form.nap_address} onChange={update('nap_address')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="123 Main Street" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">City</label>
                  <input type="text" value={form.nap_city} onChange={update('nap_city')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="Springfield" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">State</label>
                  <input type="text" value={form.nap_state} onChange={update('nap_state')} maxLength={2} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="IL" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">ZIP</label>
                  <input type="text" value={form.nap_zip} onChange={update('nap_zip')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="62701" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone Number</label>
                <input type="tel" value={form.nap_phone} onChange={update('nap_phone')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="(555) 123-4567" />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={creating || !connectionVerified}
            className="px-6 py-2.5 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {creating ? 'Creating...' : 'Create Workspace'}
          </button>
          <Link to="/workspaces" className="px-6 py-2.5 bg-navy-800 hover:bg-navy-700 text-slate-300 font-medium rounded-lg border border-navy-600 transition-colors">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
